// 離線資料儲存管理
const OFFLINE_STORAGE_KEY = 'nfc_clock_offline_data';
const MAX_OFFLINE_ENTRIES = 100;

// 離線打卡資料結構
export const offlineStorage = {
  // 儲存離線打卡資料
  async saveClockRecord(record) {
    try {
      const existingData = await this.getAllRecords();
      const newRecord = {
        ...record,
        id: Date.now(),
        createdAt: new Date().toISOString(),
        synced: false
      };
      
      existingData.push(newRecord);
      
      // 保持資料量在限制內
      if (existingData.length > MAX_OFFLINE_ENTRIES) {
        existingData.splice(0, existingData.length - MAX_OFFLINE_ENTRIES);
      }
      
      localStorage.setItem(OFFLINE_STORAGE_KEY, JSON.stringify(existingData));
      
      // 觸發更新事件
      this.triggerSyncEvent();
      
      return newRecord;
    } catch (error) {
      console.error('儲存離線資料失敗:', error);
      throw error;
    }
  },
  
  // 獲取所有離線資料
  async getAllRecords() {
    try {
      const data = localStorage.getItem(OFFLINE_STORAGE_KEY);
      return data ? JSON.parse(data) : [];
    } catch (error) {
      console.error('讀取離線資料失敗:', error);
      return [];
    }
  },
  
  // 獲取未同步的資料
  async getUnsyncedRecords() {
    const allRecords = await this.getAllRecords();
    return allRecords.filter(record => !record.synced);
  },
  
  // 標記資料為已同步
  async markAsSynced(recordId) {
    try {
      const allRecords = await this.getAllRecords();
      const updatedRecords = allRecords.map(record => 
        record.id === recordId ? { ...record, synced: true, syncedAt: new Date().toISOString() } : record
      );
      
      localStorage.setItem(OFFLINE_STORAGE_KEY, JSON.stringify(updatedRecords));
      return true;
    } catch (error) {
      console.error('標記同步狀態失敗:', error);
      return false;
    }
  },
  
  // 刪除已同步的資料
  async deleteSyncedRecords() {
    try {
      const allRecords = await this.getAllRecords();
      const unsyncedRecords = allRecords.filter(record => !record.synced);
      
      localStorage.setItem(OFFLINE_STORAGE_KEY, JSON.stringify(unsyncedRecords));
      return true;
    } catch (error) {
      console.error('刪除已同步資料失敗:', error);
      return false;
    }
  },
  
  // 清除所有離線資料
  async clearAllRecords() {
    try {
      localStorage.removeItem(OFFLINE_STORAGE_KEY);
      return true;
    } catch (error) {
      console.error('清除離線資料失敗:', error);
      return false;
    }
  },
  
  // 檢查是否有未同步的資料
  async hasUnsyncedRecords() {
    const unsynced = await this.getUnsyncedRecords();
    return unsynced.length > 0;
  },
  
  // 觸發同步事件
  triggerSyncEvent() {
    if (navigator.onLine) {
      const event = new CustomEvent('offlineDataAvailable');
      window.dispatchEvent(event);
    }
  },
  
  // 同步離線資料到伺服器
  async syncOfflineRecords() {
    if (!navigator.onLine) {
      console.log('離線狀態，無法同步');
      return { success: false, message: '離線狀態，無法同步' };
    }
    
    try {
      const unsyncedRecords = await this.getUnsyncedRecords();
      
      if (unsyncedRecords.length === 0) {
        return { success: true, message: '沒有需要同步的資料', syncedCount: 0 };
      }
      
      let syncedCount = 0;
      let failedCount = 0;
      
      for (const record of unsyncedRecords) {
        try {
          // 這裡應該調用實際的API
          // 暫時模擬成功
          console.log('同步離線資料:', record);
          
          // 標記為已同步
          await this.markAsSynced(record.id);
          syncedCount++;
          
          // 延遲以避免過多請求
          await new Promise(resolve => setTimeout(resolve, 100));
        } catch (error) {
          console.error('同步單筆資料失敗:', error, record);
          failedCount++;
        }
      }
      
      // 刪除已同步的資料
      if (syncedCount > 0) {
        await this.deleteSyncedRecords();
      }
      
      return { 
        success: true, 
        message: `同步完成：成功 ${syncedCount} 筆，失敗 ${failedCount} 筆`,
        syncedCount,
        failedCount
      };
    } catch (error) {
      console.error('同步失敗:', error);
      return { success: false, message: '同步失敗', error: error.message };
    }
  }
};

// 自動同步（當網路恢復時）
window.addEventListener('online', async () => {
  console.log('網路恢復，開始同步離線資料...');
  
  try {
    const result = await offlineStorage.syncOfflineRecords();
    if (result.success && result.syncedCount > 0) {
      console.log('離線資料同步完成:', result.message);
      
      // 顯示通知
      const notification = document.createElement('div');
      notification.className = 'sync-notification success';
      notification.innerHTML = `
        <span>✅ ${result.message}</span>
        <button onclick="this.parentElement.remove()">×</button>
      `;
      notification.style.cssText = `
        position: fixed;
        top: 20px;
        right: 20px;
        background: #4caf50;
        color: white;
        padding: 12px 20px;
        border-radius: 8px;
        box-shadow: 0 4px 12px rgba(0,0,0,0.2);
        z-index: 10001;
        display: flex;
        align-items: center;
        gap: 10px;
        animation: slideInRight 0.3s ease-out;
      `;
      
      document.body.appendChild(notification);
      
      // 5秒後自動移除
      setTimeout(() => {
        if (notification.parentElement) {
          notification.remove();
        }
      }, 5000);
    }
  } catch (error) {
    console.error('自動同步失敗:', error);
  }
});

// 監聽離線資料事件
window.addEventListener('offlineDataAvailable', async () => {
  if (navigator.onLine) {
    await offlineStorage.syncOfflineRecords();
  }
});

export default offlineStorage;